import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getProperties, getTenants } from '../api';
import Tooltip from './Tooltip';
import LoadingState from './LoadingState';
import EmptyState from './EmptyState';
import ConfirmModal from './ConfirmModal';

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
}

interface Tenant {
  id: string;
  first_name: string;
  last_name: string;
}

interface Lease {
  id: string;
  custom_id?: string;
  property_id: string;
  tenant_id?: string;
  start_date?: string;
  end_date?: string;
  rent_amount?: number;
  lease_document_url?: string;
  extracted_data?: any; // This will hold the JSON data from Document AI
  status: string;
  lease_type?: string;
  lease_term_months?: number;
  notes?: string;
  properties?: { // Nested property data from the join
    address: string;
    city: string;
    state: string;
  };
}

export const Leases: React.FC = () => {
  const navigate = useNavigate();
  const [leases, setLeases] = useState<Lease[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [message, setMessage] = useState('');
  const [editingLease, setEditingLease] = useState<Partial<Lease> | null>(null);
  const [showExtractedData, setShowExtractedData] = useState<any | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false); // New state for create form visibility
  const [newLease, setNewLease] = useState<Partial<Lease>>({}); // New state for new lease data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leaseToDelete, setLeaseToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchLeases();
    fetchPropertiesAndTenants();
  }, []);

  const getTenantName = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? `${tenant.first_name} ${tenant.last_name}` : 'N/A';
  };

  const fetchPropertiesAndTenants = async () => {
    try {
      const [propertiesResponse, tenantsResponse] = await Promise.all([
        getProperties(),
        getTenants()
      ]);
      console.log('Fetched Properties:', propertiesResponse);
      console.log('Fetched Tenants:', tenantsResponse);
      setProperties(propertiesResponse);
      setTenants(tenantsResponse);
    } catch (err) {
      console.error('Error fetching properties or tenants:', err);
      // Handle error appropriately
    }
  };

  const handleNewLeaseInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewLease(prev => ({ ...prev, [name]: value }));
  };

      const handleCreateLease = async (e: React.FormEvent) => {
          e.preventDefault();
          setMessage('');
          try {
              await api.post('/leases', newLease);
              setMessage('Lease created successfully!');
              setNewLease({}); // Clear form
              setShowCreateForm(false); // Hide form
              fetchLeases(); // Refresh list
          } catch (error: any) {
              console.error('Error creating lease:', error.response?.data || error.message);
              let errorMessage = 'Error creating lease.';
              if (error.response?.data?.detail) {
                  if (Array.isArray(error.response.data.detail)) {
                      errorMessage = error.response.data.detail.map((err: any) => {
                          if (err.loc && err.loc.length > 1) {
                              const field = err.loc[1]; // e.g., 'tenant_id'
                              return `${field.replace(/_/g, ' ')}: ${err.msg}`;
                          }
                          return err.msg;
                      }).join('; ');
                  } else if (typeof error.response.data.detail === 'string') {
                      errorMessage = error.response.data.detail;
                  }
              }
              setMessage(errorMessage);
          }
      };
  const fetchLeases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/leases');
      setLeases(response.data);
    } catch (err: any) {
      console.error('Error fetching leases:', err.response?.data || err.message);
      setError(typeof err.response?.data?.detail === 'object' ? JSON.stringify(err.response.data.detail) : err.response?.data?.detail || 'Error fetching leases.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditingLease(prev => ({ ...prev, [name]: value }));
  };

  const handleEditClick = (lease: Lease) => {
    setEditingLease(lease);
    setMessage('');
  };

  const handleUpdateLease = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLease?.id) return;
    setMessage('');
    try {
      await api.put(`/leases/${editingLease.id}`, editingLease);
      setMessage('Lease updated successfully!');
      setEditingLease(null);
      fetchLeases();
    } catch (error: any) {
      console.error('Error updating lease:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error updating lease.');
    }
  };

  const handleDeleteLease = (leaseId: string) => {
    setLeaseToDelete(leaseId);
  };

  const confirmDeleteLease = async () => {
    if (!leaseToDelete) return;
    setMessage('');
    try {
      await api.delete(`/leases/${leaseToDelete}`);
      setMessage('Lease deleted successfully!');
      fetchLeases();
    } catch (error: any) {
      console.error('Error deleting lease:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error deleting lease.');
    } finally {
      setLeaseToDelete(null);
    }
  };

  const handleViewExtractedData = (data: any) => {
    setShowExtractedData(data);
  };

  const closeExtractedDataModal = () => {
    setShowExtractedData(null);
  };

  if (loading) {
    return <LoadingState message="Loading leases..." fullScreen />;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-danger">{t("leases.k1")} {error}</div>;
  }

  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("leases.k2")}</h2>
        <Tooltip text="This page provides a comprehensive overview of all your leases. You can add new leases manually, or use the 'Lease Uploader' and 'Lease Abstraction' features to automate the process. You can also edit and delete existing leases." />
      </div>

      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showCreateForm ? t("leases.k3") : t("leases.k4")}
        </button>
        <button
          onClick={() => navigate('/bulk-leases')}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {t("leases.k5")} </button>
        <button
          onClick={() => navigate('/lease-abstraction')}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {t("leases.k6")} </button>
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}

      {showCreateForm && (
        <div className="mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
          <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("leases.k4")}</h3>
          <form onSubmit={handleCreateLease} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label htmlFor="property_id" className="text-text-secondary">
                {t("leases.k7")} </label>
              <select id="property_id" name="property_id" value={newLease.property_id || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" required>
                <option value="">{t("leases.k8")}</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>{property.address}, {property.city}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="custom_id" className="text-text-secondary">{t("leases.k9")}</label>
              <input type="text" id="custom_id" name="custom_id" placeholder={t("leases.k10")} value={newLease.custom_id || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="tenant_id" className="text-text-secondary">
                {t("leases.k11")} </label>
              <select id="tenant_id" name="tenant_id" value={newLease.tenant_id || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full text-black" required>
                <option value="" className="text-black">{t("leases.k12")}</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id} className="text-black">{tenant.first_name} {tenant.last_name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="start_date" className="text-text-secondary">{t("leases.k13")}</label>
              <input type="date" id="start_date" name="start_date" value={newLease.start_date || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="end_date" className="text-text-secondary">{t("leases.k14")}</label>
              <input type="date" id="end_date" name="end_date" value={newLease.end_date || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="rent_amount" className="text-text-secondary">{t("leases.k15")}</label>
              <input type="number" id="rent_amount" name="rent_amount" placeholder={t("leases.k16")} value={newLease.rent_amount || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="lease_type" className="text-text-secondary">{t("leases.k17")}</label>
              <input type="text" id="lease_type" name="lease_type" placeholder={t("leases.k18")} value={newLease.lease_type || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="lease_term_months" className="text-text-secondary">{t("leases.k19")}</label>
              <input type="number" id="lease_term_months" name="lease_term_months" placeholder={t("leases.k20")} value={newLease.lease_term_months || ''} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="status" className="text-text-secondary">{t("leases.k21")}</label>
              <select id="status" name="status" value={newLease.status || 'active'} onChange={handleNewLeaseInputChange} className="border border-border-primary p-2 rounded w-full">
                <option value="active">{t("leases.k22")}</option>
                <option value="pending_review">{t("leases.k23")}</option>
                <option value="expired">{t("leases.k24")}</option>
                <option value="terminated">{t("leases.k25")}</option>
              </select>
            </div>
            <textarea name="notes" placeholder={t("leases.k26")} value={newLease.notes || ''} onChange={handleNewLeaseInputChange} className="col-span-full border border-border-primary p-2 rounded w-full"></textarea>
            <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
              {t("leases.k27")} </button>
            <button type="button" onClick={() => setShowCreateForm(false)} className="col-span-full bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary">
              {t("leases.k28")} </button>
          </form>
        </div>
      )}

      {editingLease && (
        <div className="mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
          <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("leases.k29")} {editingLease.id?.substring(0,8)}...)</h3>
          <form onSubmit={handleUpdateLease} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label htmlFor="property_id" className="text-text-secondary">{t("leases.k30")}</label>
              <select id="property_id" name="property_id" value={editingLease.property_id || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" required>
                <option value="">{t("leases.k8")}</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>{property.address}, {property.city}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="custom_id" className="text-text-secondary">{t("leases.k9")}</label>
              <input type="text" id="custom_id" name="custom_id" placeholder={t("leases.k10")} value={editingLease.custom_id || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="tenant_id" className="text-text-secondary">{t("leases.k11")}</label>
              <select id="tenant_id" name="tenant_id" value={editingLease.tenant_id || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full text-black">
                <option value="" className="text-black">{t("leases.k31")}</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id} className="text-black">{tenant.first_name} {tenant.last_name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="start_date" className="text-text-secondary">{t("leases.k13")}</label>
              <input type="date" id="start_date" name="start_date" value={editingLease.start_date?.split('T')[0] || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="end_date" className="text-text-secondary">{t("leases.k14")}</label>
              <input type="date" id="end_date" name="end_date" value={editingLease.end_date?.split('T')[0] || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="rent_amount" className="text-text-secondary">{t("leases.k15")}</label>
              <input type="number" id="rent_amount" name="rent_amount" placeholder={t("leases.k16")} value={editingLease.rent_amount || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="lease_type" className="text-text-secondary">{t("leases.k17")}</label>
              <input type="text" id="lease_type" name="lease_type" placeholder={t("leases.k18")} value={editingLease.lease_type || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="lease_term_months" className="text-text-secondary">{t("leases.k19")}</label>
              <input type="number" id="lease_term_months" name="lease_term_months" placeholder={t("leases.k20")} value={editingLease.lease_term_months || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
            </div>
            <div className="flex flex-col">
              <label htmlFor="status" className="text-text-secondary">{t("leases.k21")}</label>
              <select id="status" name="status" value={editingLease.status || 'active'} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full">
                <option value="active">{t("leases.k22")}</option>
                <option value="pending_review">{t("leases.k23")}</option>
                <option value="expired">{t("leases.k24")}</option>
                <option value="terminated">{t("leases.k25")}</option>
              </select>
            </div>
            <textarea name="notes" placeholder={t("leases.k26")} value={editingLease.notes || ''} onChange={(e) => handleInputChange(e as any)} className="col-span-full border border-border-primary p-2 rounded w-full"></textarea>
            <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
              {t("leases.k32")} </button>
            <button type="button" onClick={() => setEditingLease(null)} className="col-span-full bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary">
              {t("leases.k28")} </button>
          </form>
        </div>
      )}


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leases.length === 0 ? (
          <div className="col-span-full">
            <EmptyState 
              title={t("leases.k33")}
              description="Upload a lease document via the Lease Abstraction tab to automatically extract data, or add one manually."
              icon={<svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            />
          </div>
        ) : (
          leases.map((lease) => {
            const tenantName = lease.tenant_id ? getTenantName(lease.tenant_id) : (lease.extracted_data?.tenant_name || 'N/A');

            return (
              <div key={lease.id} className="bg-bg-secondary p-4 rounded shadow border border-border-primary flex flex-col justify-between">
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-text-primary">{t("leases.k34")} {lease.custom_id || lease.id.substring(0, 8)}...</h4>
                  {lease.custom_id && <p className="text-sm text-text-secondary mb-1">{t("leases.k35")} {lease.id.substring(0, 8)}...</p>}
                  {lease.properties && (
                    <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k30")}</strong> {lease.properties.address}</p>
                  )}
                  <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k11")}</strong> {tenantName}</p>
                  <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k36")}</strong> {lease.extracted_data?.landlord_name || 'N/A'}</p>
                  <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k37")}</strong> {lease.start_date || 'N/A'} {t("leases.k38")} {lease.end_date || 'N/A'}</p>
                  <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k39")}</strong> ${lease.rent_amount ? lease.rent_amount.toFixed(2) : 'N/A'}/month</p>
                  <p className="text-sm text-text-secondary mb-1"><strong>{t("leases.k40")}</strong> ${lease.extracted_data?.security_deposit ? parseFloat(lease.extracted_data.security_deposit).toFixed(2) : 'N/A'}</p>
                  {lease.status && (
                    <p className="text-sm text-text-secondary"><strong>{t("leases.k21")}</strong> <span className={`font-semibold ${lease.status === 'active' ? 'text-green-500' : 'text-yellow-500'}`}>{lease.status}</span></p>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => handleEditClick(lease)} className="bg-green-500 border border-green-500 text-white px-4 py-2 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">{t("leases.k41")}</button>
                  <button onClick={() => handleDeleteLease(lease.id)} className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700">{t("leases.k42")}</button>
                  {lease.lease_document_url && <a href={lease.lease_document_url} target="_blank" rel="noopener noreferrer" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">{t("leases.k43")}</a>}
                  {lease.extracted_data && <button onClick={() => handleViewExtractedData(lease.extracted_data)} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">{t("leases.k44")}</button>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {showExtractedData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: '#2c2c2c', // Assuming bg-bg-secondary
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            maxWidth: '450px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("leases.k45")}</h3>
            <pre style={{
              backgroundColor: '#333333', // Assuming bg-bg-tertiary
              padding: '16px',
              borderRadius: '4px',
              border: '1px solid #444444', // Assuming border-border-primary
              color: '#cccccc', // Assuming text-text-secondary
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              flexGrow: 1,
              overflowY: 'auto'
            }}>
              {JSON.stringify(showExtractedData, null, 2)}
            </pre>
            <button
              onClick={closeExtractedDataModal}
              className="mt-4 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
            >
              {t("leases.k46")} </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!leaseToDelete}
        title={t("leases.k47")}
        message="Are you sure you want to delete this lease? This action cannot be undone."
        onConfirm={confirmDeleteLease}
        onCancel={() => setLeaseToDelete(null)}
        confirmText="Delete Lease"
      />
    </div>
  );
};
